import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  Phone,
  MessageCircle,
  ArrowRight,
  Factory,
  ShieldCheck,
  BadgeCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  navigationMenuTriggerStyle,
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Toaster, toast } from "@/components/ui/sonner";

import { COMPANY, PRODUCT_CATEGORIES, normalizePhoneForWa } from "@/mock";

const Container = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const NavItem = ({ to, children }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `text-sm font-semibold tracking-tight transition-colors hover:text-foreground ${
        isActive ? "text-foreground" : "text-muted-foreground"
      }`
    }
  >
    {children}
  </NavLink>
);

function makeWhatsAppUrl(phone, text) {
  const n = normalizePhoneForWa(phone);
  const q = new URLSearchParams();
  if (text) q.set("text", text);
  return `https://wa.me/${n}?${q.toString()}`;
}

function makeTelUrl(phone) {
  return `tel:${String(phone || "").replace(/\s/g, "")}`;
}

function makeMailUrl(email) {
  return `mailto:${email}`;
}

export const SiteLayout = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const waLeadText = useMemo(() => {
    const base = `Hello National Wire Products, I want a quotation.`;
    const page = location.pathname === "/" ? "Home" : location.pathname;
    return `${base}\nPage: ${page}`;
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile drawer ONLY when the route actually changes.
  // This prevents the “opens then immediately closes” bug on some mobile browsers.
  // Defer close to next tick to satisfy lint and avoid cascade renders.
  const prevPathRef = React.useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      const t = setTimeout(() => setMobileOpen(false), 0);
      return () => clearTimeout(t);
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster richColors />

      {/* Top strip */}
      <div className="hidden md:block border-b border-border/60 bg-secondary/40">
        <Container className="py-2 flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <BadgeCheck className="h-4 w-4 text-emerald-600" />
              {COMPANY.certification}
            </span>
            <span className="text-muted-foreground/60">•</span>
            <span className="inline-flex items-center gap-2">
              <Factory className="h-4 w-4 text-slate-600" />
              Manufacturing in {COMPANY.locationShort}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden lg:flex items-center gap-2">
              {(COMPANY.phones || [COMPANY.phone]).map((p) => (
                <a
                  key={p}
                  className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 px-3 py-1 text-xs font-semibold text-foreground/90 shadow-sm backdrop-blur"
                  href={makeTelUrl(p)}
                >
                  <Phone className="h-3.5 w-3.5" />
                  {p}
                </a>
              ))}
            </div>

            {/* Mobile/Tablet: show primary only */}
            <a
              className="lg:hidden inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 px-3 py-1 text-xs font-semibold text-foreground/90 shadow-sm backdrop-blur"
              href={makeTelUrl(COMPANY.phone)}
            >
              <Phone className="h-3.5 w-3.5" />
              {COMPANY.phone}
            </a>

            <a
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200/60 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-950 shadow-sm"
              href={makeWhatsAppUrl(COMPANY.whatsapp, waLeadText)}
              target="_blank"
              rel="noreferrer"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              WhatsApp
            </a>
          </div>
        </Container>
      </div>

      {/* Header */}
      <div
        className={`sticky top-0 z-50 border-b transition-colors ${
          scrolled
            ? "border-border/60 bg-background/75 shadow-[0_8px_30px_rgba(15,23,42,0.08)] backdrop-blur-xl"
            : "border-transparent bg-background"
        }`}
      >
        <Container>
          <div className="h-16 flex items-center justify-between gap-4">
            <Link
              to="/"
              className="group inline-flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <div className="h-12 w-12 rounded-xl border border-border/60 bg-white shadow-sm overflow-hidden">
                <img
                  src="/images/logo.png"
                  alt="National Wire Products logo"
                  className="h-full w-full object-contain p-1.5"
                  loading="eager"
                />
              </div>
              <div className="leading-tight">
                <div className="text-sm font-extrabold tracking-tight font-display">
                  {COMPANY.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {COMPANY.locationShort}
                </div>
              </div>
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-2">
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link className={navigationMenuTriggerStyle()} to="/">
                        Home
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link className={navigationMenuTriggerStyle()} to="/about">
                        About
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger className={navigationMenuTriggerStyle()}>
                      Products
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[680px] p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <div className="text-sm font-extrabold tracking-tight">
                              Product Categories
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Explore our range of steel wires, fencing & geotechnical solutions
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate("/products")}
                          >
                            View all <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          {PRODUCT_CATEGORIES.map((c) => (
                            <Link
                              key={c.id}
                              to={`/products/${c.id}`}
                              className="group rounded-xl border border-border/60 bg-background/60 overflow-hidden transition-all hover:border-amber-300 hover:shadow-md"
                              data-testid={`nav-category-${c.id}`}
                            >
                              <div className="aspect-[4/3] relative overflow-hidden bg-secondary/30">
                                <img
                                  src={c.image}
                                  alt={c.name}
                                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                <div className="absolute bottom-2 left-2 right-2">
                                  <div className="text-sm font-bold text-white drop-shadow-lg">
                                    {c.name}
                                  </div>
                                </div>
                              </div>
                              <div className="p-2.5">
                                <div className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                                  {c.short}
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link className={navigationMenuTriggerStyle()} to="/applications">
                        Applications
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link className={navigationMenuTriggerStyle()} to="/quality">
                        Quality, Trust & Certifications
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link className={navigationMenuTriggerStyle()} to="/contact">
                        Contact
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>

              <div className="ml-2 flex items-center gap-2">
                <Button
                  variant="outline"
                  className="border-emerald-200/60 bg-emerald-50 text-emerald-950 hover:bg-emerald-100"
                  onClick={() => {
                    window.open(makeWhatsAppUrl(COMPANY.whatsapp, waLeadText), "_blank");
                  }}
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  WhatsApp
                </Button>

                <Button
                  className="bg-amber-500 text-slate-950 hover:bg-amber-400"
                  onClick={() => navigate("/request-quote")}
                >
                  Request a Quote
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Mobile */}
            <div className="lg:hidden flex items-center gap-2">
              <Button
                variant="outline"
                className="border-border/70 bg-background/60 backdrop-blur"
                onClick={() => {
                  window.location.href = makeTelUrl(COMPANY.phone);
                }}
              >
                <Phone className="h-4 w-4" />
              </Button>

              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="border-border/70 bg-background/60 backdrop-blur">
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="p-0 overflow-y-auto">
                  <div className="p-6">
                    <SheetHeader>
                      <SheetTitle className="flex items-center justify-between">
                        <span>{COMPANY.name}</span>
                        <Badge className="bg-emerald-100 text-emerald-950 border-emerald-200">
                          {COMPANY.certification}
                        </Badge>
                      </SheetTitle>
                    </SheetHeader>

                    <div className="mt-6 grid gap-4">
                      <div className="grid gap-2">
                        <NavItem to="/">Home</NavItem>
                        <NavItem to="/about">About</NavItem>
                      </div>

                      {/* Product Categories with Thumbnails */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-foreground">Products</span>
                          <Link 
                            to="/products" 
                            className="text-xs text-amber-600 font-medium hover:text-amber-700"
                          >
                            View all →
                          </Link>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {PRODUCT_CATEGORIES.map((c) => (
                            <Link
                              key={c.id}
                              to={`/products/${c.id}`}
                              className="group rounded-lg border border-border/60 bg-background/60 overflow-hidden transition-all hover:border-amber-300"
                              data-testid={`mobile-nav-category-${c.id}`}
                            >
                              <div className="aspect-square relative overflow-hidden bg-secondary/30">
                                <img
                                  src={c.image}
                                  alt={c.name}
                                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                                <div className="absolute bottom-1 left-1 right-1">
                                  <div className="text-[10px] font-bold text-white drop-shadow-lg leading-tight">
                                    {c.name}
                                  </div>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>

                      <div className="grid gap-2">
                        <NavItem to="/applications">Applications</NavItem>
                        <NavItem to="/quality">Quality, Trust & Certifications</NavItem>
                        <NavItem to="/contact">Contact</NavItem>
                      </div>

                      <Separator />

                      <div className="grid gap-2">
                        <Button
                          className="bg-amber-500 text-slate-950 hover:bg-amber-400"
                          onClick={() => navigate("/request-quote")}
                        >
                          Request a Quote
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>

                        <Button
                          variant="outline"
                          className="border-emerald-200/60 bg-emerald-50 text-emerald-950 hover:bg-emerald-100"
                          onClick={() => window.open(makeWhatsAppUrl(COMPANY.whatsapp, waLeadText), "_blank")}
                        >
                          <MessageCircle className="mr-2 h-4 w-4" />
                          WhatsApp us
                        </Button>

                        <Button
                          variant="outline"
                          onClick={() => (window.location.href = makeMailUrl(COMPANY.email))}
                        >
                          Email: {COMPANY.email}
                        </Button>
                      </div>

                      <div className="rounded-xl border border-border/60 bg-secondary/40 p-4">
                        <div className="flex items-start gap-2 text-sm font-semibold">
                          <ShieldCheck className="mt-0.5 h-4 w-4 text-emerald-600" />
                          Reliable. Durable. Secure.
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {COMPANY.years}+ years of manufacturing experience. Based in Guwahati, Assam. One of the
                          market leaders in Northeast India, supplying quality products for different infrastructure
                          and fencing projects.
                        </p>
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </Container>
      </div>

      {/* Page content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border/60 bg-secondary/30">
        <Container className="py-12">
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl border border-border/60 bg-white shadow-sm overflow-hidden">
                  <img
                    src="/images/logo.png"
                    alt="National Wire Products logo"
                    className="h-full w-full object-contain p-1.5"
                    loading="lazy"
                  />
                </div>
                <div className="text-base font-extrabold tracking-tight font-display">{COMPANY.name}</div>
              </div>
              <p className="mt-2 text-sm text-muted-foreground max-w-prose">
                Manufacturer & supplier of steel wires, fencing products and geotechnical
                structures for infrastructure contractors, EPCs, distributors and industrial
                buyers across Northeast India.
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Key cities: Guwahati • Itanagar • Shillong • Imphal • Dimapur • Kohima • Aizawl • Agartala • Dibrugarh • Jorhat • Tinsukia • Silchar • Tezpur • Tawang • Dharmanagar • Aalong • Pasighat • Golaghat • Goalpara • Mankachar • Churchandpur • Moirang • Bishnupur • Ukhrul • Senapati • Dhubri • Tura • Jowai • Namsai • Champhai
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <Badge className="bg-slate-900 text-slate-50">Manufacturing in Guwahati</Badge>
                <Badge className="bg-emerald-100 text-emerald-950 border-emerald-200">
                  {COMPANY.certification}
                </Badge>
                <Badge className="bg-amber-100 text-amber-900 border-amber-200">Bulk Orders</Badge>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="text-sm font-bold">Quick Links</div>
              <div className="mt-3 grid gap-2 text-sm">
                <Link className="text-muted-foreground hover:text-foreground" to="/products">
                  Products
                </Link>
                <Link className="text-muted-foreground hover:text-foreground" to="/applications">
                  Applications
                </Link>
                <Link className="text-muted-foreground hover:text-foreground" to="/quality">
                  Quality, Trust & Certifications
                </Link>
                <Link className="text-muted-foreground hover:text-foreground" to="/contact">
                  Contact
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="text-sm font-bold">Contact</div>
              <div className="mt-3 grid gap-2 text-sm text-muted-foreground">
                <div>{COMPANY.addressLine}</div>
                <div>{COMPANY.addressLine2}</div>
                <div>
                  <div className="text-xs font-semibold text-foreground/80">Phone</div>
                  <div className="mt-1 grid gap-1">
                    {(COMPANY.phoneContacts || (COMPANY.phones || [COMPANY.phone]).map((p) => ({ label: "Phone", phone: p }))).map(
                      (c) => (
                        <a
                          key={`${c.label}-${c.phone}`}
                          className="flex items-center justify-between gap-3 rounded-xl border border-border/50 bg-background/60 px-3 py-2 hover:bg-secondary/40"
                          href={makeTelUrl(c.phone)}
                        >
                          <span className="font-semibold">{c.label}:</span>
                          <span className="font-semibold text-foreground">{c.phone}</span>
                        </a>
                      )
                    )}
                  </div>
                </div>
                <a
                  className="hover:text-foreground"
                  href={makeWhatsAppUrl(COMPANY.whatsapp, waLeadText)}
                  target="_blank"
                  rel="noreferrer"
                >
                  WhatsApp: {COMPANY.whatsapp}
                </a>
                <a className="hover:text-foreground" href={makeMailUrl(COMPANY.email)}>
                  Email: {COMPANY.email}
                </a>
              </div>

              <div className="mt-4 flex gap-2">
                <Button
                  className="bg-amber-500 text-slate-950 hover:bg-amber-400"
                  onClick={() => navigate("/request-quote")}
                >
                  Request a Quote
                </Button>
                <Button
                  variant="outline"
                  className="border-emerald-200/60 bg-emerald-50 text-emerald-950 hover:bg-emerald-100"
                  onClick={() => {
                    toast("Opening WhatsApp…", {
                      description: "We’ll pre-fill your message with page context.",
                    });
                    window.open(makeWhatsAppUrl(COMPANY.whatsapp, waLeadText), "_blank");
                  }}
                >
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>

          <Separator className="my-8" />

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
            </div>
            <div className="text-xs text-muted-foreground">
              Serving contractors, government departments, EPCs & distributors across Northeast India.
            </div>
          </div>
        </Container>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border/60 bg-background/75 backdrop-blur-xl lg:hidden">
        <div className="mx-auto max-w-7xl px-4 py-2 flex items-center gap-2">
          <Button
            className="flex-1 bg-amber-500 text-slate-950 hover:bg-amber-400"
            onClick={() => navigate("/request-quote")}
          >
            Request Quote
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="flex-1 border-emerald-200/60 bg-emerald-50 text-emerald-950 hover:bg-emerald-100"
            onClick={() => window.open(makeWhatsAppUrl(COMPANY.whatsapp, waLeadText), "_blank")}
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            WhatsApp
          </Button>
        </div>
      </div>

      {/* Spacer for sticky bar */}
      <div className="h-16 lg:hidden" />
    </div>
  );
};
